import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {
    ConfiguratorWorkflowService,
    DatastoreDto,
    EngineTagService,
    Partner,
    TagDTO,
    WorkflowConf
} from '@tj/tj-dmp-lib-api';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

@Component({
    selector: 'dmp-workflow',
    templateUrl: './workflow-old.component.html',
    styleUrls: ['./workflow-old.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class WorkflowOldComponent implements OnInit {

    breadcrumb = [
        {label: this.translate.instant('menu.flow.label')},
        {label: this.translate.instant('menu.flow.workflows')}
    ];
    description = 'Voici la liste des Workflow par groupe.';

    group: DatastoreDto;
    partner: Partner;

    actual: WorkflowConf;
    data: WorkflowConf[];
    datastore: DatastoreDto;
    id: string;
    selectedWorkflows: WorkflowConf[] = [];
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];

    @ViewChild('workflowFileInput') workflowFileInput: ElementRef;

    items = [
        {
            label: 'Edition',
            items: [{
                label: 'Détails',
                icon: 'fas fa-pen',
                command: () => {
                    this.router.navigate(['/h/workflow/edit/', this.datastore.code, this.id]);
                }
            },
               {
                    label: 'Supprimer',
                    icon: 'far fa-trash-alt',
                    command: (event) => {
                        this.doDelete(event.originalEvent, this.id);
                    }
                }
                ]
        }];

    constructor(private router: Router,
                private confirmationService: ConfirmationService,
                private translate: TranslateService,
                private messageService: MessageService,
                private configuratorWorkflowService: ConfiguratorWorkflowService,
                private fb: UntypedFormBuilder) {
    }

    ngOnInit(): void {
        console.log('ok');
        this.initExportMenu();
    }

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: 'Importer un fichier',
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: 'Exporter la sélection',
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedWorkflows(),
                disabled: true
            },
            {
                label: 'Exporter tout',
                icon: 'fas fa-download',
                command: () => this.exportAllWorkflows(),
                disabled: false
            }
        ];
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        this.updateListTable();
    }

    private updateListTable() {
        if (this.datastore) {
            this.configuratorWorkflowService.getAllWorkflows(this.datastore.code, {
                page: 0,
                size: 30
            })
                .subscribe((res) => {
                    this.data = (res as any)._embedded?.workflowDTOList;
                });
        } else {
            this.data = [];
        }
    }



    doNouveau() {
        this.router.navigate(['/h/workflow/new', this.datastore.code]);
    }


    private doDelete(event, id: string) {
        this.confirmationService.confirm({
            target: event.target,
            key: 'workflow',
            message: this.translate.instant('common.confirm'),
            acceptLabel: this.translate.instant('common.True'),
            rejectLabel: this.translate.instant('common.False'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.configuratorWorkflowService.deleteWorkflow(this.datastore.code, id).subscribe(() => {
                    this.updateListTable();
                });
            },
            reject: () => {
                // not yet
            }
        });
    }

    // ============ EXPORT/IMPORT METHODS ============

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.workflowFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Format de fichier non supporté. Veuillez utiliser .json ou .zip'
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.configuratorWorkflowService.importWorkflowJson(this.datastore.code, jsonContent).subscribe(
                (workflow) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Succès',
                        detail: `Workflow "${workflow.name}" importé avec succès`
                    });
                    this.updateListTable();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erreur',
                        detail: error.error?.detail || 'Erreur lors de l\'importation'
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.configuratorWorkflowService.importWorkflowsZip(this.datastore.code, file).subscribe(
            (workflows) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Succès',
                    detail: `${workflows.length} workflow(s) importé(s) avec succès`
                });
                this.updateListTable();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: error.error?.detail || 'Erreur lors de l\'importation'
                });
            }
        );
    }

    /**
     * Exporte un seul workflow en JSON
     */
    exportWorkflowJson(workflow: WorkflowConf) {
        this.configuratorWorkflowService.exportWorkflowJson(this.datastore.code, workflow.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `workflow-${workflow.code}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: 'Succès',
                    detail: 'Workflow exporté avec succès'
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Erreur lors de l\'exportation'
                });
            }
        );
    }

    /**
     * Exporte plusieurs workflows en ZIP
     */
    exportSelectedWorkflows() {
        if (!this.selectedWorkflows || this.selectedWorkflows.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: 'Aucun workflow sélectionné'
            });
            return;
        }

        const ids = this.selectedWorkflows.map(w => w.id);
        this.configuratorWorkflowService.exportWorkflowsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `workflows-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: 'Succès',
                    detail: `${this.selectedWorkflows.length} workflow(s) exporté(s) avec succès`
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Erreur lors de l\'exportation'
                });
            }
        );
    }

    /**
     * Exporte tous les workflows en ZIP
     */
    exportAllWorkflows() {
        if (!this.data || this.data.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: 'Aucun workflow à exporter'
            });
            return;
        }

        const ids = this.data.map(w => w.id);
        this.configuratorWorkflowService.exportWorkflowsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `workflows-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: 'Succès',
                    detail: `${this.data.length} workflow(s) exporté(s) avec succès`
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Erreur lors de l\'exportation'
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedWorkflows || this.selectedWorkflows.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.data || this.data.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }
}
